import React, { useMemo, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const backgroundImg = "/photos/photos/photo10.jpg";

const jobOptions = [
  { value: "Portrait", label: "Portrait" },
  { value: "Event", label: "Event" },
  { value: "Wedding", label: "Wedding" },
  { value: "Brand", label: "Brand Shoot" },
];

const Contact = () => {
  const [name, setName] = useState("");
  const [contact, setContact] = useState(""); // email or phone
  const [selectedJob, setSelectedJob] = useState(null);
  const [shootDate, setShootDate] = useState(null);
  const [message, setMessage] = useState("");

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        backgroundColor: "transparent",
        border: "none",
        borderBottom: state.isFocused
          ? "1px solid #6a1a33"
          : "1px solid rgba(255,255,255,0.3)",
        borderRadius: 0,
        boxShadow: "none",
        color: "#fff",
        minHeight: 44,
        paddingTop: "0.35rem",
        paddingBottom: "0.35rem",
        fontSize: "0.95rem",
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: "#1c1c1c",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: "1rem",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.6)",
        padding: "0.5rem 0",
        zIndex: 50,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? "rgba(255,255,255,0.06)" : "transparent",
        color: "#f0eae2",
        padding: "0.65rem 1rem",
        cursor: "pointer",
        borderLeft: state.isFocused ? "4px solid #6a1a33" : "4px solid transparent",
        fontWeight: state.isFocused ? 600 : 400,
      }),
      placeholder: (base) => ({ ...base, color: "#777" }),
      dropdownIndicator: (base) => ({
        ...base,
        color: "#999",
        ":hover": { color: "#f0eae2" },
      }),
      indicatorSeparator: () => ({ display: "none" }),
      singleValue: (base) => ({ ...base, color: "#fff" }),
      valueContainer: (base) => ({ ...base, paddingLeft: 0 }),
      input: (base) => ({ ...base, color: "#fff" }),
    }),
    []
  );

  const onSubmit = (e) => {
    e.preventDefault();

    // Minimal validation: name + contact + message
    if (!name.trim() || !contact.trim() || !message.trim()) return;

    // Hook this up to your email handler / SendGrid endpoint.
    // For now, you can keep your existing submission logic.
    console.log({ name, contact, selectedJob, shootDate, message });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 sm:px-6 py-20 sm:py-24 overflow-hidden bg-black text-[#f0eae2]">
      {/* Background blur layer */}
      <div
        className="absolute inset-0 bg-cover bg-center scale-105"
        style={{
          backgroundImage: `url(${backgroundImg})`,
          filter: "blur(12px) brightness(0.5)",
        }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-xl rounded-[28px] bg-gradient-to-br from-white/10 via-white/5 to-white/10 border border-white/20 backdrop-blur-[18px] shadow-[0_8px_40px_rgba(0,0,0,0.4)] px-5 sm:px-10 py-9 sm:py-12">
        <div className="absolute top-0 left-0 w-full h-[90px] sm:h-[110px] bg-white/10 rounded-t-[28px] blur-[40px] pointer-events-none z-10" />
        <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-black/20 to-transparent z-10 pointer-events-none rounded-b-[28px]" />

        <h1 className="relative z-20 text-3xl sm:text-5xl font-serif tracking-tight mb-3">
          Let’s Connect
        </h1>
        <p className="relative z-20 text-[#b0b0b0] text-sm sm:text-base mb-7 leading-relaxed">
          Tell me what you’re looking for. I’ll respond with availability and pricing.
        </p>

        <form onSubmit={onSubmit} className="relative z-20 space-y-5" autoComplete="off">
          {/* Name */}
          <input
            type="text"
            name="name"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-[#777] focus:outline-none focus:border-wine py-3 text-[15px] sm:text-base transition"
            required
          />

          {/* Email or Phone */}
          <input
            type="text"
            name="contact"
            placeholder="Email or Phone"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-[#777] focus:outline-none focus:border-wine py-3 text-[15px] sm:text-base transition"
            required
          />

          {/* Job Type (optional but helpful) */}
          <div className="w-full">
            <Select
              options={jobOptions}
              value={selectedJob}
              onChange={setSelectedJob}
              placeholder="Shoot Type (optional)"
              classNamePrefix="react-select"
              styles={selectStyles}
            />
          </div>

          {/* Date (optional) */}
          <div className="w-full border-b border-white/30 focus-within:border-wine py-3 transition">
            <DatePicker
              selected={shootDate}
              onChange={(date) => setShootDate(date)}
              placeholderText="Preferred Date (optional)"
              dateFormat="MM/dd/yyyy"
              className="bg-transparent w-full text-white placeholder:text-[#777] focus:outline-none text-[15px] sm:text-base"
              calendarClassName="custom-calendar"
              popperPlacement="bottom-start"
              dayClassName={() => "hover:bg-[#6a1a33] hover:text-white transition-all"}
            />
          </div>

          {/* Message */}
          <textarea
            name="message"
            rows={4}
            placeholder="What are you envisioning? (location, vibe, how many people, etc.)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-transparent border-b border-white/30 text-white placeholder:text-[#777] focus:outline-none focus:border-wine py-3 text-[15px] sm:text-base resize-none transition"
            required
          />

          {/* Submit */}
          <button
            type="submit"
            className="mt-2 w-full bg-wine hover:bg-[#741a24] text-white font-semibold tracking-wide py-3 rounded-xl transition-all duration-200 ease-in-out shadow-md transform hover:scale-[0.99] active:scale-[0.97]"
          >
            Send Message
          </button>

          <p className="text-center text-xs sm:text-sm text-white/70 pt-1">
            Or DM me on Instagram → <span className="text-[#c2a86e]">@shoot_me.eli</span>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Contact;























